import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { css, styled } from '@compiled/react';

import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import { durations } from '@atlaskit/motion/utils/durations';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';

import { useOnboardingTakeoverType } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/useOnboardingTakeoverType.ts';
import { useWebPanelLocation } from '@confluence/web-panel-location/entry-points/useWebPanelLocation.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import {
	FEED_EXPERIENCE,
	HOME_OVERVIEW_RECENT_EXPERIENCE,
	HOME_OVERVIEW_EXPERIENCE,
	HOME_RIGHT_PANEL_APPS_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceAbort,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { collectAll } from '@confluence/experience-tracker/entry-points/ExperienceEventCollector.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { ExperienceEvent } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import type { Experience } from '@confluence/experience-tracker/entry-points/Experience.ts';
import { WebPanel } from '@confluence/web-panel-location/entry-points/WebPanel.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { HOME_OVERVIEW_FEED_PAGE_SEGMENT } from '@confluence/feed/entry-points/perf.config.ts';
import { Feed } from '@confluence/feed/entry-points/Feed.ts';
import type { CustomPanel } from '@confluence/feed/entry-points/Feed.ts';
import { DASHBOARD_SECONDARY } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useTemplatesCollectionEligible } from '@confluence/onboarding-helpers/entry-points/hooks/useTemplatesCollectionEligible.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { PostOfficeHomeBanner } from '@confluence/experiment-post-office-home-banner/entry-points/PostOfficeHomeBanner.ts';
import { usePostOfficeHomeBannerEligible } from '@confluence/experiment-post-office-home-banner/entry-points/usePostOfficeHomeBannerExperiment.ts';
import { LazyAutomationExecutionLimit } from '@confluence/automation/entry-points/execution-limit.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { AgentsAwarenessModalLoadable } from '@confluence/agents-awareness-modal/entry-points/AgentsAwarenessModalLoadable.ts';

import {
	HOME_OVERVIEW_RECENT_PAGE_SEGMENT,
	HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT,
} from '../../../perf.config.ts';
import { RightPanel } from '../../RightPanel/RightPanel.tsx';
import { useRightPanelVisible } from '../../RightPanel/useRightPanelVisible.ts';
import { HomeWelcomeMessage } from '../../RightPanel/WelcomeMessage/LazyWelcomeMessage.ts';
import { useAnnouncementsPanelVisibility } from '../../RightPanel/WelcomeMessage/useAnnouncementsPanelVisibility.ts';
import { Calendars } from '../../RightPanel/Calendars.tsx';
import {
	LazyFeedbackIcon as FeedbackIcon,
	LazyCalendarIcon as CalendarIcon,
} from '../images/lazyImages.tsx';

import { AgentSessionsContainer } from './AgentSessions/AgentSessionsContainer.tsx';
import { HomeBannerContainer } from './HomeBannerContainer.tsx';
import { RovoAgentsForYouLoadable } from './RovoAgentsForYou/RovoAgentsForYouLoadable.tsx';
import { useRovoAgentsForYouEligibility } from './RovoAgentsForYou/useRovoAgentsForYouEligibility.tsx';
import { RecentList } from './RecentList.tsx';
import { RecentListError } from './RecentListError.tsx';
import { useIsEligibleForHideRecents } from './useIsEligibleForHideRecents.tsx';
import { useAbortHomeOverviewRecentOnLeave } from './useAbortHomeOverviewRecentOnLeave.ts';

const i18n = defineMessages({
	forYouTitle: {
		id: 'home.for-you.title',
		defaultMessage: 'For you',
		description: 'Title for the home page which is called "For you" in nav 4',
	},
	recentWork: {
		id: 'home.views.overview.recent-work',
		defaultMessage: 'Pick up where you left off',
		description: 'Header for a list of users recently visited pages',
	},
	calendarTitle: {
		id: 'home.feed.calendar.title',
		defaultMessage: 'Calendars',
		description: 'Tab title for the calendars section on the home feed',
	},
	announcementsTitle: {
		id: 'home.feed.announcements.title',
		defaultMessage: 'Announcements',
		description: 'Tab title for the announcements section on the home feed',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const OverviewContainer = styled.div<{
	isOnboardingExperienceEligible: boolean;
	isPostOfficeHomeBannerEligible: boolean;
	shouldShowRightPanel?: boolean;
}>({
	width: '100%',
	display: 'grid',
	gridColumnGap: token('space.400'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridTemplateRows: ({ isPostOfficeHomeBannerEligible }) =>
		isPostOfficeHomeBannerEligible ? 'auto auto auto 1fr' : 'auto auto 1fr',
	/* If --homeIsSingleColumn == 0 then include a 300px sidebar */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridTemplateColumns: ({ shouldShowRightPanel }) =>
		shouldShowRightPanel
			? `calc((100% - calc((1 - var(--homeIsSingleColumn)) * ${token(
					'space.400',
				)}) - (1 - var(--homeIsSingleColumn)) * 300px)) calc((1 - var(--homeIsSingleColumn)) * 300px)`
			: '100% 0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	transition: ({ shouldShowRightPanel }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		shouldShowRightPanel ? `${durations.medium}ms` : 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-has-onboarding-experience': ({ isOnboardingExperienceEligible }) =>
		isOnboardingExperienceEligible ? '1' : '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-show-post-office-banner': ({ isPostOfficeHomeBannerEligible }) =>
		isPostOfficeHomeBannerEligible ? '1' : '0',
});

const recentContainerStyles = css({
	position: 'relative',
	gridColumn: '1 / span 1',
	// Above Condition and If --home-show-template-collections == 1 then row = 4 else row = 3

	gridRow:
		'(1 + var(--home-has-onboarding-experience) + var(--home-show-template-collections) + var(--home-show-rovo-awareness-banner)) / span 1',
	marginTop: '0px',

	marginBottom: token('space.300'),
});

const bannerContainerStyles = css({
	gridColumn: '1 / span 1',
	// If --homeIsSingleColumn == 1 then row = 3 else row = 2
	// Above Condition and If --home-has-onboarding-experience == 1 then row = 4 else row = 3

	gridRow: `calc(
			2 + var(--homeIsSingleColumn) + var(--home-has-onboarding-experience) +
				var(--home-show-template-collections) +
				var(--home-show-rovo-awareness-banner)
		) / span 1`,
});

// Rendered above the banners segment (see JSX order). Pinned to the main column so
// grid auto-placement does not squeeze it into the sidebar.
const rovoAgentsContainerStyles = css({
	gridColumn: '1 / span 1',
});

const feedContainerStyles = css({
	gridColumn: '1 / span 1',
	// If --homeIsSingleColumn == 1 then row = 3 else row = 2
	// Above Condition and If --home-has-onboarding-experience == 1 then row = 4 else row = 3
	// Above Condition and If --home-show-post-office-banner == 1 then row = 5 else row = 4

	gridRow: `calc(
			2 + var(--homeIsSingleColumn) + var(--home-has-onboarding-experience) +
				var(--home-show-template-collections) + var(--home-show-post-office-banner)
				+ var(--home-show-rovo-awareness-banner)
		) / span 1`,
});

const rightPanelContainerStyles = css({
	marginTop:
		'calc(34px * (1 - var(--homeIsSingleColumn)) * (1 - var(--home-has-onboarding-experience)))',
	transition: 'margin-top 0.5s',
	// If --homeIsSingleColumn == 1 then col = 1 else col = 2

	gridColumn: `2`,
	// If --homeIsSingleColumn == 1 then row = 2 / span 1 else row = 1 / span 2

	gridRowStart: `1`,

	gridRowEnd: '4',

	minWidth: '300px',
});

const recentHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: token('space.200'),
});

const limitContainerStyles = css({
	position: 'fixed',
	left: token('space.1000'),
	zIndex: layers.modal() + 50,
	bottom: token('space.1000'),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.small'),
});

export const collectOverview = (events: ExperienceEvent[], experience: Experience): void => {
	const collection = [
		FEED_EXPERIENCE,
		HOME_OVERVIEW_RECENT_EXPERIENCE,
		HOME_RIGHT_PANEL_APPS_EXPERIENCE,
	];
	collectAll(collection)(events, experience);
};

const RecentHeader = () => (
	<div css={recentHeaderStyles}>
		<Heading size="xsmall" as="h2">
			<FormattedMessage {...i18n.recentWork} />
		</Heading>
	</div>
);

type FeedSectionProps = {
	customPanels: CustomPanel[];
	appsLoading: boolean;
	appsError?: unknown;
	testId?: string;
};

export const FeedSection: FC<FeedSectionProps> = ({
	customPanels,
	appsLoading,
	appsError,
	testId = 'feed-section',
}) => (
	<UFOSegment name="feed-section">
		<div css={feedContainerStyles} data-testid={testId}>
			<PageSegmentLoadStart metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
			<ExperienceStart name={FEED_EXPERIENCE} />
			<ExperienceStart name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
			<Feed renderPopularFeed customPanels={customPanels} />
			{!appsLoading && !appsError ? (
				<ExperienceSuccess name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
			) : null}
		</div>
	</UFOSegment>
);

type AnnouncementsVisibilityFeedSectionProps = {
	announcementsPanel: CustomPanel;
	feedCustomWebPanels: CustomPanel[];
	appsLoading: boolean;
	appsError?: unknown;
};

// includes the Announcements panel only if the welcome message is customized and non-empty
export const AnnouncementsVisibilityFeedSection: FC<AnnouncementsVisibilityFeedSectionProps> = ({
	announcementsPanel,
	feedCustomWebPanels,
	appsLoading,
	appsError,
}) => {
	const { loading, shouldShowAnnouncementsPanel } = useAnnouncementsPanelVisibility();
	const customPanels =
		!loading && shouldShowAnnouncementsPanel
			? [announcementsPanel, ...feedCustomWebPanels]
			: feedCustomWebPanels;

	return (
		<FeedSection
			customPanels={customPanels}
			appsLoading={appsLoading}
			appsError={appsError}
			testId="announcements-visibility-feed-section"
		/>
	);
};

//previously called Overview or homeOverview
export const ForYou: FC = () => {
	const intl = useIntl();

	const { onboardingTakeoverType } = useOnboardingTakeoverType();
	const { isTemplatesCollectionEligible } = useTemplatesCollectionEligible();
	const { isEligible: isPostOfficeHomeBannerEligible } = usePostOfficeHomeBannerEligible();
	const { shouldShowRightPanel } = useRightPanelVisible();
	const isRovoAgentsForYouEligible = useRovoAgentsForYouEligibility();
	// Passed from child panels rendered inside Feed custom panels.
	// e.g. for calendar: ForYou > Feed custom panel > Calendars > HomepageCard.
	// Feed owns the wrapper, but child panels report when they are showing an empty state.
	const [panelEmptyStates, setPanelEmptyStates] = useState<Record<string, boolean>>({});
	const handlePanelEmptyStateChange = useCallback(
		(completeKey: string, isEmptyState: boolean | null) => {
			// null means the panel is still pending, so keep the last known empty-state value.
			if (isEmptyState !== null) {
				setPanelEmptyStates((previousEmptyStates) =>
					previousEmptyStates[completeKey] === isEmptyState
						? previousEmptyStates
						: {
								...previousEmptyStates,
								[completeKey]: isEmptyState,
							},
				);
			}
		},
		[],
	);

	const isEligibleForHideRecents = useIsEligibleForHideRecents();
	useAbortHomeOverviewRecentOnLeave();

	const {
		panels,
		loading: appsLoading,
		error: appsError,
	} = useWebPanelLocation({
		//no way to conditionally load this? Because hooks can't be conditional
		location: DASHBOARD_SECONDARY,
		fetchPolicy: 'cache-first',
	});
	const sortedPanels = useMemo(
		() => (panels ? [...panels].sort(({ weight: i }, { weight: j }) => (i > j ? 1 : -1)) : []),
		[panels],
	);

	const feedCustomWebPanels = useMemo<CustomPanel[]>(
		() =>
			sortedPanels.map(({ html, completeKey, label }) => {
				const isTeamCalendarsPanel = completeKey.includes('team-calendars');
				const shouldUseCalendarEmptyStateBanner =
					isTeamCalendarsPanel &&
					expValEquals('cc_mohiti_for_you_empty_states_exp', 'isEnabled', true);
				const isPanelEmptyState = Boolean(panelEmptyStates[completeKey]);

				return isTeamCalendarsPanel
					? {
							title: intl.formatMessage(i18n.calendarTitle),
							key: completeKey,
							icon: CalendarIcon,
							content: (
								<Calendars
									completeKey={completeKey}
									key={completeKey}
									noCollapse
									showCalendarEmptyStateBanner={shouldUseCalendarEmptyStateBanner}
									keepCalendarEmptyStateBannerVisible={
										shouldUseCalendarEmptyStateBanner && isPanelEmptyState
									}
									onEmptyStateChange={
										shouldUseCalendarEmptyStateBanner
											? (isEmptyState) => handlePanelEmptyStateChange(completeKey, isEmptyState)
											: undefined
									}
								/>
							),
							renderWithoutWrapper: shouldUseCalendarEmptyStateBanner && isPanelEmptyState,
						}
					: {
							title: label,
							icon: undefined,
							key: completeKey,
							content: <WebPanel html={html} />,
						};
			}),
		[handlePanelEmptyStateChange, intl, panelEmptyStates, sortedPanels],
	);

	const announcementsPanel: CustomPanel = {
		title: intl.formatMessage(i18n.announcementsTitle),
		key: 'confluence.home.announcements',
		icon: FeedbackIcon,
		content: <HomeWelcomeMessage noCollapse />,
	};
	const feedCustomPanels: CustomPanel[] = [announcementsPanel, ...feedCustomWebPanels];

	return (
		<>
			<OverviewContainer
				isOnboardingExperienceEligible={Boolean(
					onboardingTakeoverType || isTemplatesCollectionEligible, // Only one of the two can show up at once, so we only need one styling variable
				)}
				isPostOfficeHomeBannerEligible={
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					isPostOfficeHomeBannerEligible && fg('post-office_enable-confluence-home-banner')
				}
				shouldShowRightPanel={shouldShowRightPanel}
				data-testid="home_overview"
			>
				<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.forYouTitle)} isHomePage />
				<ExperienceStart name={HOME_OVERVIEW_EXPERIENCE} collect={collectOverview} />
				<ExperienceStart name={HOME_OVERVIEW_RECENT_EXPERIENCE} timeout={ExperienceTimeout.HOME} />
				{isRovoAgentsForYouEligible && (
					<UFOSegment name="rovo-agents-for-you">
						<div css={rovoAgentsContainerStyles}>
							<RovoAgentsForYouLoadable />
						</div>
					</UFOSegment>
				)}
				<UFOSegment name="banners">
					<HomeBannerContainer
						onboardingTakeoverType={onboardingTakeoverType}
						isTemplatesCollectionEligible={isTemplatesCollectionEligible}
					/>
					<AgentSessionsContainer>
						{(isTabbed) => (
							<>
								{isEligibleForHideRecents &&
									// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
									fg('confluence_home_recent_experience_leak_fix') && (
										// The recents list, which owns this experience's stop, is not rendered. A failed recents
										// query also lands here: for new users it is treated as having no recent history.
										<ExperienceAbort
											name={HOME_OVERVIEW_RECENT_EXPERIENCE}
											reason="Recents hidden"
										/>
									)}
								{!isEligibleForHideRecents && (
									<div css={recentContainerStyles}>
										<PageSegmentLoadStart metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
										<>
											{!isTabbed && <RecentHeader />}
											<GenericErrorBoundary
												attribution={Attribution.DISCO}
												renderOnError={RecentListError}
											>
												<RecentList />
											</GenericErrorBoundary>
										</>
									</div>
								)}
							</>
						)}
					</AgentSessionsContainer>
					{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
					{isPostOfficeHomeBannerEligible && fg('post-office_enable-confluence-home-banner') && (
						<div css={bannerContainerStyles}>
							<PostOfficeHomeBanner />
						</div>
					)}
				</UFOSegment>
				{expValEquals('cc_mohiti_for_you_empty_states_exp', 'isEnabled', true) ? (
					<AnnouncementsVisibilityFeedSection
						announcementsPanel={announcementsPanel}
						feedCustomWebPanels={feedCustomWebPanels}
						appsLoading={appsLoading}
						appsError={appsError}
					/>
				) : (
					<FeedSection
						customPanels={feedCustomPanels}
						appsLoading={appsLoading}
						appsError={appsError}
					/>
				)}
				{shouldShowRightPanel && (
					<div css={rightPanelContainerStyles}>
						<PageSegmentLoadStart metric={HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT} />
						<RightPanel widthMode="fullwidth" />
					</div>
				)}
			</OverviewContainer>
			<UFOSegment name="automation-limit">
				<div css={limitContainerStyles}>
					<LazyAutomationExecutionLimit />
				</div>
			</UFOSegment>
			{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
			{fg('post-office_enable_cc-agents-awareness-modal') && <AgentsAwarenessModalLoadable />}
		</>
	);
};
